import React from "react";
import { Box, Chip, styled, useTheme } from "@mui/material";
import isPropValid from "@emotion/is-prop-valid";
import { TypographyWithInfoIcon } from "@rentablo/components";
import { InfoChip } from "./types";

type InfoChipsSize = "xsmall" | "small" | "medium";
type InfoChipsDirection = "row" | "column";

const ChipsRowContainer = styled(Box, {
  shouldForwardProp: (prop: string) => isPropValid(prop) || prop !== "size",
})<{
  size: InfoChipsSize;
}>(({ theme, size }) => ({
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  alignItems: "center",
  gap: theme.spacing(size === "xsmall" ? 1 : 2.5),
  overflow: "auto",
  "&::-webkit-scrollbar": {
    display: "none",
  },
}));

const ChipsColumnContainer = styled(Box, {
  shouldForwardProp: (prop: string) => isPropValid(prop) || prop !== "size",
})<{
  size: InfoChipsSize;
}>(({ theme, size }) => ({
  display: "flex",
  flexDirection: "column",
  flexWrap: "nowrap",
  alignItems: "flex-start",
  gap: theme.spacing(size === "xsmall" ? 1 : 2.5),
  overflow: "auto",
  "&::-webkit-scrollbar": {
    display: "none",
  },
}));

const StyledChip = styled(Chip, {
  shouldForwardProp: (prop: string) =>
    isPropValid(prop) || (prop !== "chipColor" && prop !== "chipTextColor"),
})<{
  chipColor?: string;
  chipTextColor?: string;
}>(({ theme, chipColor, chipTextColor }) => ({
  backgroundColor: chipColor,
  color: chipTextColor,
  borderRadius: theme.spacing(4),
  ".MuiChip-label": {
    color: chipTextColor,
    padding: theme.spacing(1, 3),
  },
  svg: {
    color: chipTextColor,
  },
}));

interface Props {
  options: InfoChip[];
  size?: InfoChipsSize;
  direction?: InfoChipsDirection;
  noWrapLabels?: boolean;
}

export const InfoChips = ({
  options,
  size = "small",
  direction = "row",
  noWrapLabels = false,
}: Props) => {
  const theme = useTheme();
  const showBorder = (type: InfoChip) => !type.color && !type.textColor;
  const Container =
    direction === "column" ? ChipsColumnContainer : ChipsRowContainer;

  return (
    <Container size={size}>
      {options.map((type, index) => {
        const labelElement = (
          <TypographyWithInfoIcon
            variant={size === "xsmall" ? "caption" : "body3"}
            tooltipText={type.tooltip}
            sx={{
              color: type.textColor ?? theme.palette.text.secondary,
              ...(noWrapLabels && {
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                overflow: "hidden",
              }),
            }}
          >
            {type.text}
          </TypographyWithInfoIcon>
        );

        return (
          <StyledChip
            key={index}
            size={size}
            chipColor={type.color ?? theme.palette.background.paper}
            chipTextColor={type.textColor ?? theme.palette.text.primary}
            icon={type.icon}
            sx={{
              border: showBorder(type)
                ? `1px solid ${theme.palette.border.secondary}`
                : "none",
            }}
            label={labelElement}
          />
        );
      })}
    </Container>
  );
};
